/**
 * New console — the two numbers on the rail.
 *
 * The badge on Triage queue and the one on the bell. Both are read once when the
 * shell mounts and again whenever the clinic scope changes, never on a timer: a
 * console that re-polls in the background makes a lead list that already takes a
 * minute noticeably worse, and neither number changes second to second.
 *
 * They fail quietly. A badge is an aid, and a shell that will not render because a
 * count endpoint was slow is a much bigger problem than a missing number.
 */
import { useCallback, useEffect, useState } from 'react';
import moment from 'moment';

import { getNotificationCountAPI } from '@services/notification';
import { DashboardApi } from '@services/dashboard';

import { flatBodyOf } from './api';

/* Whatever range the dashboard is asked for, the untriaged figure people mean is
   "what is waiting now" — so the badge counts the same window the triage queue opens
   on, the last thirty days (pages/Triage.jsx). It counted the calendar month once, and
   on the 3rd the badge said two days' worth while the queue said thirty.

   These are moment objects, not native Dates. DashboardApi formats them through
   `APIdateFormat`, which is `date.format('YYYY-MM-DD')` — a moment method. A native Date
   threw before the request was built, the error was swallowed by the service's own
   try/catch, and the badge sat at 0 permanently with nothing logged. */
export const QUEUE_DAYS = 30;
const queueStart = () => moment().subtract(QUEUE_DAYS, 'days').startOf('day');

/**
 * Fired by the alert log when it marks something read, so the bell in the shell can
 * re-read its count. Exported so both sides name the same string.
 */
const ALERTS_READ = 'newconsole:alerts-read';

export const announceAlertsRead = () => window.dispatchEvent(new Event(ALERTS_READ));

const useShellCounts = (clinicId, canSeeAlerts, canTriage) => {
  const [untriaged, setUntriaged] = useState(0);
  const [unread, setUnread] = useState(0);

  const loadUnread = useCallback(() => {
    if (!canSeeAlerts) return;
    getNotificationCountAPI().then((res) => {
      setUnread(res && res.isOk ? Number(res.notif_count) || 0 : 0);
    });
  }, [canSeeAlerts]);

  useEffect(() => { loadUnread(); }, [loadUnread]);

  useEffect(() => {
    if (!canTriage) return undefined;
    let live = true;
    DashboardApi({ startDate: queueStart(), endDate: moment().endOf('day'), clinic_id: clinicId || 0 })
      .then((res) => {
        if (!live) return;
        const d = flatBodyOf(res);
        setUntriaged(Number(d.Untriaged) || 0);
      })
      .catch(() => { /* a missing badge is not worth breaking the shell over */ });
    return () => { live = false; };
  }, [clinicId, canTriage]);

  /* The alert log needs to clear the bell the moment somebody reads an alert, otherwise
     the badge sits there contradicting the screen they are looking at until a reload.
     It is mounted by the router with no props, so rather than thread a callback down
     through the route table for one number, the screen announces it and the shell
     listens. One event, one listener, and neither side holds a reference to the other. */
  useEffect(() => {
    const onRead = () => loadUnread();
    window.addEventListener(ALERTS_READ, onRead);
    return () => window.removeEventListener(ALERTS_READ, onRead);
  }, [loadUnread]);

  /* A tab left open on one device keeps its old number while alerts are read on another.
     Coming back to the tab re-reads it — on return, not on a timer. */
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') loadUnread(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', loadUnread);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', loadUnread);
    };
  }, [loadUnread]);

  /* Still returned: a screen that already knows the new figure can set it directly and
     skip the round trip. */
  return { untriaged, unread, refreshUnread: loadUnread, setUnread };
};

export default useShellCounts;
